// memberDetail/view.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import LanguageIcon from '@mui/icons-material/Language';
import dayjs from 'dayjs';
import {
  StyledDialog,
  StyledDialogContent,
  MemberDetailHeader,
  MemberDetailContent,
  MemberAvatarContainer,
  InfoSection,
  LinksSection,
  AdminActionsSection,
  RoleChangeDialog,
  RemoveConfirmDialog
} from './styled';

const MemberDetailView = ({
  member,
  open,
  onClose,
  isAdmin,
  onMemberAction
}) => {
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('PA');
  const [removeReason, setRemoveReason] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  if (!member) return null;

  // Process member data
  const processedMember = {
    ...member,
    name: member.user ?
      `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || 'Unknown Member' :
      member.name || 'Unknown Member',
    alias: member.user?.alias || '',
    description: member.user?.description || member.bio || '',
    avatar: member.user?.avatar || member.avatar || '',
    role: member.role || 'PA',
    status: member.status || 'AC',
    isVerified: member.user?.is_verified || member.isVerified || false,
    joinedDate: member.create_date || dayjs().subtract(30, 'day').format(),
    links: {
      link1: member.user?.link_1,
      link2: member.user?.link_2,
      link3: member.user?.link_3
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'AC':
        return { label: 'Active', color: 'success', icon: <CheckCircleIcon /> };
      case 'PP':
        return { label: 'Pending', color: 'warning', icon: <PendingIcon /> };
      case 'RE':
        return { label: 'Rejected', color: 'error', icon: <CancelIcon /> };
      case 'IN':
        return { label: 'Inactive', color: 'default', icon: <PendingIcon /> };
      case 'BA':
        return { label: 'Banned', color: 'error', icon: <CancelIcon /> };
      default:
        return { label: 'Unknown', color: 'default', icon: <PendingIcon /> };
    }
  };

  const roleNames = {
    'FO': 'Founder',
    'AD': 'Admin',
    'MA': 'Manager',
    'PT': 'Partner',
    'EO': 'Event Organizer',
    'PA': 'Member',
    'GU': 'Guest'
  };

  const statusInfo = getStatusInfo(processedMember.status);

  const handleRoleChange = () => {
    setNewRole(processedMember.role);
    setRoleDialogOpen(true);
  };

  const handleRoleConfirm = () => {
    if (onMemberAction && newRole !== processedMember.role) {
      onMemberAction('changeRole', { newRole });
    }
    setRoleDialogOpen(false);
  };

  const handleRemoveMember = () => {
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = () => {
    if (onMemberAction) {
      onMemberAction('remove', { reason: removeReason });
    }
    setRemoveDialogOpen(false);
    onClose();
  };

  const formatLink = (link) => {
    if (!link) return null;
    if (link.startsWith('http://') || link.startsWith('https://')) {
      return link;
    }
    return `https://${link}`;
  };

  const hasLinks = processedMember.links.link1 || processedMember.links.link2 || processedMember.links.link3;

  // Function to check if description needs truncation
  const shouldTruncateDescription = (text) => {
    if (!text) return false;
    const lines = text.split('\n');
    return lines.length > 5 || text.length > 400;
  };

  const getTruncatedDescription = (text) => {
    if (!text) return '';
    const lines = text.split('\n').slice(0, 5);
    let truncated = lines.join('\n');
    if (truncated.length > 400) {
      truncated = truncated.substring(0, 400);
    }
    return truncated;
  };

  const needsTruncation = shouldTruncateDescription(processedMember.description);
  const displayDescription = isDescriptionExpanded || !needsTruncation
    ? processedMember.description
    : getTruncatedDescription(processedMember.description);

  return (
    <>
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        {/* Header Section */}
        <MemberDetailHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <MemberAvatarContainer>
              <Avatar
                src={processedMember.avatar}
                sx={{ width: 80, height: 80, fontSize: '2rem' }}
              >
                {processedMember.name.charAt(0).toUpperCase()}
              </Avatar>
              {processedMember.isVerified && (
                <VerifiedIcon
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    color: 'primary.main',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    fontSize: 24
                  }}
                />
              )}
            </MemberAvatarContainer>
            
            <Box sx={{ ml: 3, flex: 1 }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                {processedMember.name}
              </Typography>
              
              {processedMember.alias && (
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  "{processedMember.alias}"
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Chip
                  size="medium"
                  color={statusInfo.color}
                  label={statusInfo.label}
                  icon={statusInfo.icon}
                  variant={processedMember.status === 'AC' ? 'filled' : 'outlined'}
                />
                
                <Chip
                  size="medium"
                  label={roleNames[processedMember.role] || 'Member'}
                  icon={['FO', 'AD', 'MA', 'PT', 'EO'].includes(processedMember.role) ? 
                    <AdminPanelSettingsIcon /> : null}
                  variant="outlined"
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                Joined {dayjs(processedMember.joinedDate).format('MMMM D, YYYY')}
              </Typography>
            </Box>
          </Box>
          
          <IconButton onClick={onClose} sx={{ alignSelf: 'flex-start' }}>
            <CloseIcon />
          </IconButton>
        </MemberDetailHeader>

        {/* Main Content */}
        <StyledDialogContent>
          <MemberDetailContent>
            <Grid container spacing={4}>
              {/* Left Column - About */}
              <Grid item xs={12} md={hasLinks ? 6 : 12}>
                <InfoSection>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    About
                  </Typography>
                  
                  {processedMember.description ? (
                    <>
                      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {displayDescription}
                        {needsTruncation && !isDescriptionExpanded && '...'}
                      </Typography>
                      {needsTruncation && (
                        <Button
                          size="small"
                          onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                          sx={{ 
                            textTransform: 'none',
                            color: 'primary.main',
                            fontWeight: 600,
                            padding: 0,
                            minWidth: 'auto',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {isDescriptionExpanded ? 'Show less' : 'Read more'}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                      No description provided
                    </Typography>
                  )}
                </InfoSection>
              </Grid>

              {/* Right Column - Links (if any exist) */}
              {hasLinks && (
                <Grid item xs={12} md={6}>
                  <LinksSection>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Links
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {processedMember.links.link1 && (
                        <Link
                          href={formatLink(processedMember.links.link1)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={processedMember.links.link1}
                        >
                          <LanguageIcon fontSize="small" sx={{ flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {processedMember.links.link1}
                          </span>
                        </Link>
                      )}
                      {processedMember.links.link2 && (
                        <Link
                          href={formatLink(processedMember.links.link2)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={processedMember.links.link2}
                        >
                          <LanguageIcon fontSize="small" sx={{ flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {processedMember.links.link2}
                          </span>
                        </Link>
                      )}
                      {processedMember.links.link3 && (
                        <Link
                          href={formatLink(processedMember.links.link3)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={processedMember.links.link3}
                        >
                          <LanguageIcon fontSize="small" sx={{ flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {processedMember.links.link3}
                          </span>
                        </Link>
                      )}
                    </Box>
                  </LinksSection>
                </Grid>
              )}
            </Grid>

            {/* Admin Actions Section - Only visible to admins */}
            {isAdmin && (
              <Grid container spacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <AdminActionsSection>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Admin Actions
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={<AdminPanelSettingsIcon />}
                        onClick={handleRoleChange}
                      >
                        Change Role
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<PersonRemoveIcon />}
                        onClick={handleRemoveMember}
                      >
                        Remove Member
                      </Button>
                    </Box>
                  </AdminActionsSection>
                </Grid>
              </Grid>
            )}
          </MemberDetailContent>
        </StyledDialogContent>
      </StyledDialog>

      {/* Role Change Dialog */}
      <RoleChangeDialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
        <DialogTitle>Change Member Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Change the role for {processedMember.name}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>New Role</InputLabel>
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              label="New Role"
            >
              <MenuItem value="PA">Member</MenuItem>
              <MenuItem value="MA">Manager</MenuItem>
              <MenuItem value="AD">Admin</MenuItem>
              <MenuItem value="FO">Founder</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRoleConfirm} variant="contained">
            Change Role
          </Button>
        </DialogActions>
      </RoleChangeDialog>

      {/* Remove Member Dialog */}
      <RemoveConfirmDialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to remove {processedMember.name} from the community?
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={3}
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            placeholder="Provide a reason for removing this member..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Remove Member
          </Button>
        </DialogActions>
      </RemoveConfirmDialog>
    </>
  );
};

export default MemberDetailView;